import React, { useMemo, useState } from 'react';
import { Header } from './components/Header';
import { FilterBar } from './components/FilterBar';
import { QueueTable } from './components/QueueTable';
import { BorrowerPanel } from './components/BorrowerPanel';
import { mockBorrowers, Borrower } from './data/borrowers';
export function App() {
  const [borrowers, setBorrowers] = useState(mockBorrowers);
  const [selectedBorrower, setSelectedBorrower] = useState<Borrower | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedManagers, setSelectedManagers] = useState<string[]>([]);
  const [selectedLoas, setSelectedLoas] = useState<string[]>([]);
  const [selectedLoanTypes, setSelectedLoanTypes] = useState<string[]>([]);
  const [fundedDateFrom, setFundedDateFrom] = useState('');
  const [fundedDateTo, setFundedDateTo] = useState('');
  const filteredBorrowers = useMemo(() => {
    return borrowers.filter(borrower => {
      const matchesSearch = borrower.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = selectedStatus === 'All' || borrower.status === selectedStatus;
      const matchesManager = selectedManagers.length === 0 || selectedManagers.includes(borrower.manager);
      const matchesLoa = selectedLoas.length === 0 || selectedLoas.includes(borrower.loa);
      const matchesLoanType = selectedLoanTypes.length === 0 || selectedLoanTypes.includes(borrower.loanType);
      const matchesDateFrom = !fundedDateFrom || borrower.fundedDate >= fundedDateFrom;
      const matchesDateTo = !fundedDateTo || borrower.fundedDate <= fundedDateTo;
      return matchesSearch && matchesStatus && matchesManager && matchesLoa && matchesLoanType && matchesDateFrom && matchesDateTo;
    });
  }, [borrowers, searchQuery, selectedStatus, selectedManagers, selectedLoas, selectedLoanTypes, fundedDateFrom, fundedDateTo]);
  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const todayBorrowers = borrowers.filter(b => b.lastActivity === today);
    return {
      activeQueue: borrowers.length,
      attemptedToday: todayBorrowers.filter(b => b.status === 'Attempted').length,
      connectedToday: todayBorrowers.filter(b => b.status === 'Connected').length,
      convertedToday: todayBorrowers.filter(b => b.status === 'Converted').length,
      conversionRate: Math.round(borrowers.filter(b => b.status === 'Converted').length / borrowers.length * 100)
    };
  }, [borrowers]);
  const handleStatusChange = (borrowerId: string, newStatus: 'Attempted' | 'Connected' | 'Converted') => {
    setBorrowers(prev => prev.map(b => b.id === borrowerId ? {
      ...b,
      status: newStatus,
      lastActivity: new Date().toISOString().split('T')[0],
      engagementHistory: [{
        id: `e${Date.now()}`,
        type: 'status',
        title: `Status changed to ${newStatus}`,
        timestamp: new Date().toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      }, ...b.engagementHistory]
    } : b));
    if (selectedBorrower?.id === borrowerId) {
      setSelectedBorrower(prev => prev ? {
        ...prev,
        status: newStatus,
        lastActivity: new Date().toISOString().split('T')[0]
      } : null);
    }
  };
  const handleCreateLoan = (borrowerId: string) => {
    alert(`Creating new loan for borrower ${borrowerId}`);
  };
  const handleMarkComplete = (borrowerId: string) => {
    handleStatusChange(borrowerId, 'Converted');
  };
  const handleSaveNote = (borrowerId: string, note: string) => {
    setBorrowers(prev => prev.map(b => b.id === borrowerId ? {
      ...b,
      engagementHistory: [{
        id: `e${Date.now()}`,
        type: 'note',
        title: 'Note added',
        timestamp: new Date().toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        }),
        body: note
      }, ...b.engagementHistory]
    } : b));
    if (selectedBorrower?.id === borrowerId) {
      const updatedBorrower = borrowers.find(b => b.id === borrowerId);
      if (updatedBorrower) {
        setSelectedBorrower({
          ...updatedBorrower,
          engagementHistory: [{
            id: `e${Date.now()}`,
            type: 'note',
            title: 'Note added',
            timestamp: new Date().toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            }),
            body: note
          }, ...updatedBorrower.engagementHistory]
        });
      }
    }
  };
  return <div className="h-screen flex flex-col bg-slate-100" data-id="element-0">
      <Header stats={stats} data-id="element-1" />

      <FilterBar searchQuery={searchQuery} onSearchChange={setSearchQuery} selectedStatus={selectedStatus} onStatusChange={setSelectedStatus} selectedManagers={selectedManagers} onManagersChange={setSelectedManagers} selectedLoas={selectedLoas} onLoasChange={setSelectedLoas} selectedLoanTypes={selectedLoanTypes} onLoanTypesChange={setSelectedLoanTypes} fundedDateFrom={fundedDateFrom} fundedDateTo={fundedDateTo} onFundedDateFromChange={setFundedDateFrom} onFundedDateToChange={setFundedDateTo} data-id="element-2" />

      <QueueTable borrowers={filteredBorrowers} onRowClick={setSelectedBorrower} onStatusChange={handleStatusChange} onCreateLoan={handleCreateLoan} onMarkComplete={handleMarkComplete} data-id="element-3" />

      <BorrowerPanel borrower={selectedBorrower} onClose={() => setSelectedBorrower(null)} onSaveNote={handleSaveNote} data-id="element-4" />
    </div>;
}